import React, { useEffect } from 'react';

interface SEOProps {
  title: string;
  description?: string; // Optional for app pages (SEO not needed)
  canonical?: string;
  ogImage?: string;
  noindex?: boolean;
}

/**
 * Composant SEO pour gérer les meta tags dynamiques par page
 * Standard pour un SaaS moderne : chaque page a ses propres meta tags
 */
const SEO: React.FC<SEOProps> = ({
  title,
  description,
  canonical,
  ogImage = 'https://leadsme.app/logo512.png',
  noindex = false,
}) => {
  useEffect(() => {
    // Update document title
    document.title = title;

    // Update or create meta description (only if provided)
    if (description) {
      let metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', description);
      } else {
        metaDescription = document.createElement('meta');
        metaDescription.setAttribute('name', 'description');
        metaDescription.setAttribute('content', description);
        document.head.appendChild(metaDescription);
      }
    }

    // Update robots meta tag
    let metaRobots = document.querySelector('meta[name="robots"]');
    if (metaRobots) {
      metaRobots.setAttribute('content', noindex ? 'noindex, nofollow' : 'index, follow');
    } else {
      metaRobots = document.createElement('meta');
      metaRobots.setAttribute('name', 'robots');
      metaRobots.setAttribute('content', noindex ? 'noindex, nofollow' : 'index, follow');
      document.head.appendChild(metaRobots);
    }

    // Update or create canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      if (canonicalLink) {
        canonicalLink.setAttribute('href', canonical);
      } else {
        canonicalLink = document.createElement('link');
        canonicalLink.setAttribute('rel', 'canonical');
        canonicalLink.setAttribute('href', canonical);
        document.head.appendChild(canonicalLink);
      }
    }

    // Update Open Graph tags
    const updateOGTag = (property: string, content: string) => {
      let ogTag = document.querySelector(`meta[property="${property}"]`);
      if (ogTag) {
        ogTag.setAttribute('content', content);
      } else {
        ogTag = document.createElement('meta');
        ogTag.setAttribute('property', property);
        ogTag.setAttribute('content', content);
        document.head.appendChild(ogTag);
      }
    };

    updateOGTag('og:title', title);
    if (description) {
      updateOGTag('og:description', description);
    }
    updateOGTag('og:image', ogImage);
    if (canonical) {
      updateOGTag('og:url', canonical);
    }
    // SEO content is always French (same URL for all UI languages)
    updateOGTag('og:locale', 'fr_FR');

    // Update Twitter tags (only if description provided)
    if (description) {
      const updateTwitterTag = (name: string, content: string) => {
        let twitterTag = document.querySelector(`meta[name="${name}"]`);
        if (twitterTag) {
          twitterTag.setAttribute('content', content);
        } else {
          twitterTag = document.createElement('meta');
          twitterTag.setAttribute('name', name);
          twitterTag.setAttribute('content', content);
          document.head.appendChild(twitterTag);
        }
      };

      updateTwitterTag('twitter:title', title);
      updateTwitterTag('twitter:description', description);
      updateTwitterTag('twitter:image', ogImage);
    }

    // Update Schema.org Organization markup (for logo in Google search results)
    let schemaScript = document.querySelector('script[type="application/ld+json"]');
    if (schemaScript) {
      try {
        const schema = JSON.parse(schemaScript.textContent || '{}');
        if (schema['@type'] === 'Organization') {
          // Ensure logo is properly formatted for Google
          if (!schema.logo || typeof schema.logo === 'string') {
            schema.logo = {
              '@type': 'ImageObject',
              'url': ogImage,
              'width': 512,
              'height': 512
            };
          } else {
            schema.logo.url = ogImage;
          }
          if (description) {
            schema.description = description;
          }
          schemaScript.textContent = JSON.stringify(schema);
        }
      } catch (e) {
        // If parsing fails, keep original schema
        console.warn('Failed to update Schema.org markup:', e);
      }
    }
  }, [title, description, canonical, ogImage, noindex]);

  return null;
};

export default SEO;
